import {
  isBankishStatement,
  isReconciled,
  isSetAsideSegment,
  isSetAsideStatement,
  isStatementSegmentStatus,
  needsReview,
} from '@/modules/projects/status'
import type {
  ReconciliationAccount,
  Statement,
  StatementSegment,
  Transaction,
} from '@/modules/projects/stores'
import type { ReconciliationAnchorDTO } from '@/modules/projects/stores/types'
import { formatDate, formatDateRange, numberToCurrency } from '@/utils'

export const accountLabel = (last4: string) => last4 || 'Unknown acct'

// A credit union bundles several shares under one member number, so two chains can print the same
// last4. Tack the share id on so they read as different accounts.
export const withShareLabel = (
  label: string,
  shareNumber: string | null | undefined,
) => (shareNumber ? `${label} · share ${shareNumber}` : label)

// Share ids reach us padded differently on each side, and older rows lost a digit ('0020' stored as
// '020'), so compare on the digits with leading zeros dropped. One digit is kept, so an all-zero
// share is '0' and stays distinct from no share at all. Mirrors lukita's normalize_share_number.
export const shareKey = (value: string | null | undefined): string | null => {
  const digits = (value ?? '').replace(/\D/g, '')
  if (digits === '') return null
  return digits.replace(/^0+(?=\d)/, '')
}

// Just enough of an account to address one on the wire, or to tell two of them apart.
export type AccountIdentity = Pick<
  ReconciliationAccount,
  'last4' | 'shareNumber'
>

// Same reason: last4 alone is not unique across shares, so it is not a safe key on its own. The
// share is normalized so the same share padded two ways reads as one account.
export const accountKey = (account: AccountIdentity) =>
  account.shareNumber
    ? `${account.last4}:${shareKey(account.shareNumber)}`
    : account.last4

const last4Of = (value: string | null | undefined) =>
  (value ?? '').replace(/\D/g, '').slice(-4)

// A row belongs to an account when its number matches the account's last-4 or one of its folded
// card members (mirrors how lukita sums the cycle's gap). Build the owned-last4 set once, then filter.
// Several credit-union shares print one last-4, so when this account names a share, a row naming a
// different one is the other chain's. A row with no share stays in: nothing says it isn't ours.
export const rowsForAccount = (
  rows: Transaction[],
  account: ReconciliationAccount,
) => {
  const owned = new Set([
    last4Of(account.last4),
    ...(account.memberLast4S ?? []).map(last4Of),
  ])
  const wantedShare = shareKey(account.shareNumber)
  return rows.filter((row) => {
    if (!owned.has(last4Of(row.accountNumber))) return false
    const rowShare = shareKey(row.shareNumber)
    return wantedShare == null || rowShare == null || rowShare === wantedShare
  })
}

export interface RowGroup {
  key: string
  last4: string
  shareNumber: string | null
  rows: Transaction[]
}

// The cycle's rows filed under the account stamped on each one. This reads nothing from the
// reconcile detail, so it still works for an account that never had its balances read. Rows keep the
// order they arrive in, so the caller's printed order carries into each group. An unplaced row has
// no account to sit under, so its group sorts last.
export const groupRowsByAccount = (rows: Transaction[]): RowGroup[] => {
  const groups = new Map<string, RowGroup>()
  for (const row of rows) {
    const last4 = last4Of(row.accountNumber)
    const share = shareKey(row.shareNumber)
    const key = share == null ? last4 : `${last4}:${share}`
    const group = groups.get(key)
    if (group) {
      group.rows.push(row)
    } else {
      groups.set(key, {
        key,
        last4,
        shareNumber: share == null ? null : (row.shareNumber ?? null),
        rows: [row],
      })
    }
  }
  // Rank first, key second: comparing two blank-account groups by key as well keeps the comparator
  // consistent, which returning 1 for either side would not.
  const unplaced = (group: RowGroup) => (group.last4 === '' ? 1 : 0)
  return [...groups.values()].sort(
    (first, second) =>
      unplaced(first) - unplaced(second) || first.key.localeCompare(second.key),
  )
}

// A row the extractor could not place sorts after every row it could, so a missing page or ordinal
// never pushes a placed row out of position.
const compareUnplacedLast = (first: number | null, second: number | null) => {
  if (first === second) return 0
  if (first == null) return 1
  if (second == null) return -1
  return first - second
}

// The review workspace reads the ledger next to the PDF page, so the rows follow the order they
// print: page first, then position on the page. Id breaks any remaining tie, so rows with equal
// keys keep one order across renders.
export const compareByPrintedOrder = (
  first: Transaction,
  second: Transaction,
) =>
  compareUnplacedLast(first.sourcePage, second.sourcePage) ||
  compareUnplacedLast(first.pageOrder, second.pageOrder) ||
  first.id - second.id

// Where the row sits on the printed page, so a user reading the ledger next to the PDF can find it.
// The spot on its own says nothing without a page, so a row missing either number gets no label.
export const printedPositionLabel = (row: Transaction): string | null =>
  row.sourcePage != null && row.pageOrder != null
    ? `p.${row.sourcePage} · ${row.pageOrder}`
    : null

// Which account a cycle with no per-account breakdown belongs to, so a user keying in its balances
// by hand does not have to look it up. The engine names the account when it saw one but could not
// check it; otherwise the statement's other cycles do, and the last4 they use most wins.
export const suggestedCycleLast4 = (
  statement: Statement,
  segment: StatementSegment,
): string => {
  const unverified =
    segment.reconciliationDetail?.unverifiedAccounts?.[0]?.last4 ??
    segment.reconciliationDetail?.unverifiedLast4S?.[0]
  if (unverified) return unverified

  const uses = new Map<string, number>()
  for (const sibling of statement.segments ?? []) {
    if (sibling.id === segment.id) continue
    for (const account of sibling.reconciliationDetail?.accounts ?? []) {
      if (!account.last4) continue
      uses.set(account.last4, (uses.get(account.last4) ?? 0) + 1)
    }
  }

  let winner = ''
  let winningUses = 0
  for (const [last4, count] of uses) {
    if (count > winningUses) {
      winner = last4
      winningUses = count
    }
  }
  return winner
}

// Body for correcting one account's printed begin or end balance. Naming the share matters when
// several of them print one last4: without it lukita edits the first chain that matches.
export const anchorEditBody = (
  account: AccountIdentity,
  side: 'begin' | 'end',
  value: number,
): ReconciliationAnchorDTO => ({
  last4: account.last4,
  ...(account.shareNumber ? { share_number: account.shareNumber } : {}),
  [side]: value,
})

export const totalGap = (accounts: { gap: number | null }[]) =>
  accounts.reduce((sum, account) => sum + Math.abs(account.gap ?? 0), 0)

// A cycle can need review with a zero gap when extra rows cancel each other out, so only a real
// gap prints an amount. "Off by $0.00" would read as balanced.
export const gapVerdict = (gap: number) =>
  gap > 0 ? `Off by ${numberToCurrency(gap)}` : 'Needs review'

// The project's other cycles filed under the same layout key, so one fix note reaches all of them.
export const layoutSiblings = (
  statements: Statement[],
  segment: StatementSegment,
): LayoutSibling[] =>
  statements.flatMap((statement) =>
    (statement.segments ?? [])
      .filter(
        (cycle) =>
          cycle.id !== segment.id &&
          !!segment.layoutKey &&
          cycle.layoutKey === segment.layoutKey,
      )
      .map((cycle) => ({ statement, segment: cycle })),
  )

export interface LayoutSibling {
  statement: Statement
  segment: StatementSegment
}

// A cycle the family run touched kept a snapshot of how it read before, stamped with the note it ran
// with. The snapshot lands when the run is enqueued, so only a finished run proves the note reached
// the cycle; a busy or failed one stays offered.
const wasFixedByNote = (sibling: LayoutSibling, note: string) =>
  sibling.segment.beforeReextract?.instruction === note &&
  isStatementSegmentStatus(sibling.segment, 'succeeded')

export const siblingsFixedByNote = (
  siblings: LayoutSibling[],
  note: string | null | undefined,
): LayoutSibling[] =>
  note ? siblings.filter((sibling) => wasFixedByNote(sibling, note)) : []

// Running the same note again on a cycle it already reached only pays for the same answer, so a
// sibling the note touched is never offered for another run, balanced or still off.
export const siblingsStillToFix = (
  siblings: LayoutSibling[],
  note: string | null | undefined,
): LayoutSibling[] =>
  siblings.filter(
    (sibling) =>
      !isReconciled(sibling.segment) &&
      !(note && wasFixedByNote(sibling, note)),
  )

// Siblings the family run has not answered for yet. A sibling answers by carrying this note's
// snapshot, which lands whether its fresh read succeeded or failed and whether the added-lines path
// found rows, so it is the only mark that proves the run reached every cycle.
export const siblingsAwaitingNote = (
  siblings: LayoutSibling[],
  note: string | null | undefined,
): LayoutSibling[] =>
  note
    ? siblings.filter(
        (sibling) => sibling.segment.beforeReextract?.instruction !== note,
      )
    : []

// Siblings that balanced on their own, so this note never had to reach them.
export const siblingsAlreadyBalanced = (
  siblings: LayoutSibling[],
  note: string | null | undefined,
): LayoutSibling[] =>
  siblings.filter(
    (sibling) =>
      isReconciled(sibling.segment) && !(note && wasFixedByNote(sibling, note)),
  )

export const periodLabel = (segment: StatementSegment) => {
  if (segment.startDate && segment.endDate) {
    return formatDateRange(segment.startDate, segment.endDate)
  }
  if (segment.startDate) return formatDate(segment.startDate)
  return segment.name || 'Unknown period'
}

// Only bank/credit-card statements carry reconcile data; checks-only uploads and documents the engine
// set aside as not a statement do not.
export const reconcilableStatements = (statements: Statement[]) =>
  statements.filter(
    (statement) =>
      isBankishStatement(statement) && !isSetAsideStatement(statement),
  )

// A cycle set aside as not a bank statement has no rows and no balances, so it is not a reconcile job.
// Drop it before any card, count or worklist reads a statement's cycles.
export const reconcilableSegments = (statement: Statement) =>
  (statement.segments ?? []).filter((segment) => !isSetAsideSegment(segment))

// A "range" is a single billing cycle. Count the cycles (across all statements) that the
// engine could not balance, so the needs-attention notice can headline them.
export const cyclesNeedingReview = (statements: Statement[]) =>
  reconcilableStatements(statements).reduce((total, statement) => {
    const segments = reconcilableSegments(statement)
    if (segments.length === 0) return total + (needsReview(statement) ? 1 : 0)
    return total + segments.filter(needsReview).length
  }, 0)
